import React from "react";
import { useMoney } from "../../../../hooks/useMoney";
import { PrincipalFormData, PrincipalFormErrors } from "../types";

interface AdditionalSettingsFormProps {
  formData: PrincipalFormData;
  formErrors: PrincipalFormErrors;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

const AdditionalSettingsForm: React.FC<AdditionalSettingsFormProps> = ({
  formData,
  formErrors,
  onChange,
}) => {
  const { code } = useMoney();

  return (
    <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
      <h2 className="text-lg font-semibold mb-4">
        Additional Settings
      </h2>
      <div className="space-y-6">
        {/* Principal Conversion Rate */}
        <div>
          <label htmlFor="principalConversionRate" className="block text-sm font-medium text-gray-700 mb-2">
            Principal Conversion Rate
          </label>
          <input
            id="principalConversionRate"
            type="number"
            name="principalConversionRate"
            value={formData.principalConversionRate}
            onChange={onChange}
            placeholder="1.0000"
            min="0"
            step="0.0001"
            className={`block w-full px-3 py-2 border ${formErrors.principalConversionRate ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
          />
          {formErrors.principalConversionRate && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.principalConversionRate}
            </div>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Default: 1.0000
          </p>
        </div>

        {/* Code Activation Fee */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="codeActivationFeeAmount" className="block text-sm font-medium text-gray-700 mb-2">
              Code Activation Fee Amount
            </label>
            <input
              id="codeActivationFeeAmount"
              type="number"
              name="codeActivationFeeAmount"
              value={formData.codeActivationFeeAmount}
              onChange={onChange}
              placeholder="0.00"
              min="0"
              step="0.01"
              className={`block w-full px-3 py-2 border ${formErrors.codeActivationFeeAmount ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.codeActivationFeeAmount && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.codeActivationFeeAmount}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="codeActivationFeeCurrency" className="block text-sm font-medium text-gray-700 mb-2">
              Code Activation Fee Currency
            </label>
            <select
              id="codeActivationFeeCurrency"
              name="codeActivationFeeCurrency"
              value={formData.codeActivationFeeCurrency}
              onChange={onChange}
              className={`block w-full px-3 py-2 border ${formErrors.codeActivationFeeCurrency ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand appearance-none`}
            >
              <option value={code}>{code}</option>
              {formData.codeActivationFeeCurrency &&
                ![code, "USD", "INT"].includes(formData.codeActivationFeeCurrency) && (
                  <option value={formData.codeActivationFeeCurrency}>{formData.codeActivationFeeCurrency}</option>
                )}
              <option value="USD">USD</option>
              <option value="INT">INT</option>
            </select>
            {formErrors.codeActivationFeeCurrency && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.codeActivationFeeCurrency}
              </div>
            )}
          </div>
        </div>

        {/* Redemption Details */}
        <div>
          <label htmlFor="redemptionDetails" className="block text-sm font-medium text-gray-700 mb-2">
            Redemption Details
          </label>
          <textarea
            id="redemptionDetails"
            name="redemptionDetails"
            value={formData.redemptionDetails}
            onChange={onChange}
            placeholder="Redeem at any of our partner stores."
            rows={3}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
          />
          <p className="text-sm text-gray-500 mt-1">
            Instructions for redemption
          </p>
        </div>

        {/* Checkboxes */}
        <div className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="useLogo"
              checked={formData.useLogo}
              onChange={onChange}
              className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Use Logo
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="redemptionNotification"
              checked={formData.redemptionNotification}
              onChange={onChange}
              className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Redemption Notification
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="codeValidityBalanceCheck"
              checked={formData.codeValidityBalanceCheck}
              onChange={onChange}
              className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-2"
            />
            <span className="text-sm font-medium text-gray-700">
              Code Validity Balance Check
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default AdditionalSettingsForm;

